import React, { RefObject, SetStateAction, useCallback, useRef, useState } from 'react';

type DropdownType = HTMLDivElement | HTMLButtonElement;

export interface DropdownResult<T extends DropdownType> {
  readonly dropdownRef: RefObject<T | null>;
  readonly isDropdownOpen: boolean;
  readonly setIsDropdownOpen: (value: SetStateAction<boolean>) => void;
  readonly focusedOptionIdx: number;
  readonly onKeyDownHandler: (event: React.KeyboardEvent<DropdownType>) => void;
  readonly onBlurHandler: (event: React.FocusEvent<DropdownType>) => void;
  readonly listboxId: string;
}

export default function useDropdownHandler<T extends DropdownType>(
  id: string,
  optionsLength: number,
  initialFocusedOptionIdx: number = 0
): DropdownResult<T> {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef: RefObject<T | null> = useRef<T>(null);
  const [focusedOptionIdx, setFocusedOptionIdx] = useState<number>(initialFocusedOptionIdx);
  const listboxId: string = `listboxId-${id}`;

  if (!isDropdownOpen && initialFocusedOptionIdx !== focusedOptionIdx) {
    setFocusedOptionIdx(initialFocusedOptionIdx);
  }

  const getFocusedElement = useCallback((): HTMLElement | null => {
    const optionId: string = `${listboxId}-option-${focusedOptionIdx}`;
    return document.getElementById(optionId);
  }, [focusedOptionIdx, listboxId]);

  const scrollFocusedElementIntoView = useCallback(
    (optionIdx: number): void => {
      if (optionIdx < 0) {
        return;
      }

      requestAnimationFrame(() => {
        const optionId: string = `${listboxId}-option-${optionIdx}`;
        document.getElementById(optionId)?.scrollIntoView({ block: 'nearest' });
      });
    },
    [listboxId]
  );

  const onKeyDownHandler = useCallback(
    (event: React.KeyboardEvent<DropdownType>): void => {
      if (isDropdownOpen) {
        event.preventDefault();

        const closeDropdown = (): void => {
          setIsDropdownOpen(false);
        };

        switch (event.key) {
          case 'ArrowDown':
            setFocusedOptionIdx((prev: number): number => {
              const nextFocusedOptionIdx = prev < optionsLength - 1 ? prev + 1 : 0;
              scrollFocusedElementIntoView(nextFocusedOptionIdx);
              return nextFocusedOptionIdx;
            });
            break;
          case 'ArrowUp': {
            setFocusedOptionIdx((prev: number): number => {
              const nextFocusedOptionIdx = prev > -1 ? prev - 1 : -1;
              scrollFocusedElementIntoView(nextFocusedOptionIdx);
              return nextFocusedOptionIdx;
            });
            break;
          }
          case 'Escape':
          case 'Tab':
            closeDropdown();
            break;
          case 'Enter':
            const elementById: HTMLElement | null = getFocusedElement();
            if (elementById) {
              elementById.click();
            }
            closeDropdown();
            break;
        }
      }
    },
    [getFocusedElement, isDropdownOpen, optionsLength, scrollFocusedElementIntoView]
  );

  const onBlurHandler = useCallback(
    (event: React.FocusEvent<DropdownType>): void => {
      const nextTarget = event.relatedTarget;
      const isInsideDropdown =
        nextTarget instanceof Element && dropdownRef.current?.contains(nextTarget);

      if (!isInsideDropdown) {
        setIsDropdownOpen(false);
      }
    },
    [setIsDropdownOpen]
  );

  return {
    dropdownRef,
    isDropdownOpen,
    setIsDropdownOpen,
    onKeyDownHandler,
    onBlurHandler,
    focusedOptionIdx,
    listboxId
  };
}
